import { useEffect, useMemo } from 'react';
import { debounce } from 'lodash';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { indexWorkflows, workflowQueryKeys, WorkflowsIndexParams } from 'services/api/workflows';

const NUDGE_DEBOUNCE_MS = 400;
const NUDGE_MAX_WAIT_MS = 1500;

// The fallback cadence only (OLIO-2602): the WorkflowListChannel nudge is the primary
// freshness signal; this slow poll covers dropped sockets and missed events.
const FALLBACK_POLL_INTERVAL_MS = 60_000;

/**
 * The list data hook shared by the Inbound Referrals inbox and the Portal Automation inbox.
 * Params are opaque to this hook — they just key the query and go straight to the API — so a
 * page-specific tab/chip/search filter is the caller's business, not this hook's.
 */
export default function useWorkflows(params: WorkflowsIndexParams = {}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workflowQueryKeys.list(params),
    queryFn: ({ signal }) => indexWorkflows(params, signal),
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  // Live updates (OLIO-2602): one nudge per settled run in this client — refetch the list
  // through the same query so visibility and shape stay the fetch's business. Invalidates the
  // list PREFIX (not one param-keyed key) so every open page/filter/tab combination refetches,
  // not just whichever one happened to be mounted when the socket message arrived.
  // Debounced: several runs settling together (or one busy run) collapse into one
  // list refetch; the max wait bounds staleness under a steady stream.
  const nudged = useMemo(
    () =>
      debounce(() => queryClient.invalidateQueries({ queryKey: workflowQueryKeys.listPrefix }), NUDGE_DEBOUNCE_MS, {
        maxWait: NUDGE_MAX_WAIT_MS,
      }),
    [queryClient]
  );
  useEffect(() => () => nudged.cancel(), [nudged]);

  useWebsocketSubscription({
    channel: 'WorkflowListChannel',
    onReceived: () => nudged(),
  });

  return {
    workflows: query.data?.data ?? [],
    meta: query.data?.meta,
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
}
